import React from "react";
import { Link } from "react-router-dom";
import { Building2, UtensilsCrossed, ReceiptText, BarChart2, QrCode, ShieldCheck, Star, Zap, Clock, IndianRupee, Users, Heart, CheckCircle, ChevronRight } from "lucide-react";
import dinesparkLogo from "../assets/lg.png";
import "./Landing.css";

function Landing() {
  return (
    <div className="landing-page">
      {/* HERO SECTION */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        
        <nav className="landing-nav">
          <div className="landing-logo">
            <img src={dinesparkLogo} alt="Dinespark Logo" className="brand-logo-img" />
          </div>
          <div className="nav-links">
            <Link to="/login" className="btn-login-outline">Login</Link>
          </div>
        </nav>

        <div className="hero-content">
          <div className="badge">
            <Star size={16} fill="currentColor" color="#f6ad55" /> 
            Premium Hotel Management
          </div>
          <h1 className="hero-title">
            <span className="text-accent">Manage Your Hotel,</span> <br/>
            <span className="text-accent">Effortlessly</span>
          </h1>
          <p className="hero-subtitle">
            Dinespark's all-in-one billing & management system. Handle tables, menus, GST billing, and reports — all from one powerful dashboard.
          </p>
          <div className="hero-buttons">
            <Link to="/login" className="btn-primary">
              Get Started <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURES SECTION */}
      <section className="section bg-white full-width">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Everything You Need to Run Your Hotel</h2>
            <p className="section-subtitle">
              Powerful tools designed for hotel owners who want speed, accuracy, and control.
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="icon-wrapper">
                <UtensilsCrossed size={24} />
              </div>
              <h3 className="feature-title">Menu Management</h3>
              <p className="feature-desc">Add, edit & organize your full menu with categories and pricing</p>
            </div>

            <div className="feature-card">
              <div className="icon-wrapper">
                <ReceiptText size={24} />
              </div>
              <h3 className="feature-title">Smart Billing</h3>
              <p className="feature-desc">GST-ready invoices with table-wise billing and order tracking</p>
            </div>

            <div className="feature-card">
              <div className="icon-wrapper">
                <BarChart2 size={24} />
              </div>
              <h3 className="feature-title">Reports & Analytics</h3>
              <p className="feature-desc">Daily, weekly & monthly sales reports at your fingertips</p>
            </div>

            <div className="feature-card">
              <div className="icon-wrapper">
                <QrCode size={24} />
              </div>
              <h3 className="feature-title">QR Menu for Customers</h3>
              <p className="feature-desc">Contactless digital menu accessible via QR code on tables</p>
            </div>

            <div className="feature-card">
              <div className="icon-wrapper">
                <ShieldCheck size={24} />
              </div>
              <h3 className="feature-title">Secure Admin Access</h3>
              <p className="feature-desc">Role-based login to keep your data safe and controlled</p>
            </div>

            <div className="feature-card">
              <div className="icon-wrapper">
                <Star size={24} />
              </div>
              <h3 className="feature-title">Table Management</h3>
              <p className="feature-desc">Real-time table status tracking with live order previews</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="section bg-cream full-width" style={{ paddingTop: '100px' }}>
        <div className="section-container">
          <div className="section-header">
            <div className="badge-tag">
              <ShieldCheck size={16} /> Why Dinespark?
            </div>
            <h2 className="section-title">Why Choose Us?</h2>
            <p className="section-subtitle">
              We built this system specifically for Indian hotel owners who need a simple, fast, and reliable way to manage their daily operations.
            </p>
          </div>

          <div className="why-grid">
            <div className="why-item">
              <div className="why-icon">
                <Zap size={24} />
              </div>
              <div className="why-content">
                <h3>Lightning Fast Billing</h3>
                <p>Generate bills in seconds with automatic tax calculations and item search</p>
              </div>
            </div>

            <div className="why-item">
              <div className="why-icon">
                <Clock size={24} />
              </div>
              <div className="why-content">
                <h3>Save Hours Daily</h3>
                <p>Automate repetitive tasks — no more manual calculations or paper registers</p>
              </div>
            </div>

            <div className="why-item">
              <div className="why-icon">
                <IndianRupee size={24} />
              </div>
              <div className="why-content">
                <h3>GST Compliant</h3>
                <p>Auto-calculate GST on bills with proper invoice formatting for compliance</p>
              </div>
            </div>

            <div className="why-item">
              <div className="why-icon">
                <Users size={24} />
              </div>
              <div className="why-content">
                <h3>Built for Hotel Owners</h3>
                <p>Designed specifically for Indian hotels and restaurants — not generic software</p>
              </div>
            </div>

            <div className="why-item">
              <div className="why-icon">
                <Heart size={24} />
              </div>
              <div className="why-content">
                <h3>Simple & Easy to Use</h3>
                <p>No training needed — your staff can start using it from day one</p>
              </div>
            </div>

            <div className="why-item">
              <div className="why-icon">
                <CheckCircle size={24} />
              </div>
              <div className="why-content">
                <h3>All-in-One Solution</h3>
                <p>Tables, menu, billing, reports, QR menu — everything in a single dashboard</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS SECTION */}
      <section className="stats-section bg-white full-width">
        <div className="section-container" style={{ display: 'flex', justifyContent: 'space-around', width: '100%', flexWrap: 'wrap' }}>
          <div className="stat-item">
            <div className="stat-number">50+</div>
            <div className="stat-label">Tables Managed</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">1000+</div>
            <div className="stat-label">Bills Generated</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">99%</div>
            <div className="stat-label">Uptime</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">24/7</div>
            <div className="stat-label">Access Anytime</div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="cta-block">
        <h2 className="section-title"><span className="text-accent">Ready to Streamline Your Hotel?</span></h2>
        <p className="section-subtitle">
          Login now and take full control of your billing, tables, menu and reports.
        </p>
        <Link to="/login" className="btn-light mt-4">
          Go to Admin Panel <ChevronRight size={16} />
        </Link>
      </section>

      {/* FOOTER */}
      <footer className="landing-footer">
        <div className="footer-logo">
          <img src={dinesparkLogo} alt="Dinespark Logo" className="footer-brand-img" />
        </div>
        <div className="footer-copy">
          © 2026 Dinespark. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

export default Landing;
